//@ts-check
import React, { useState, memo, useMemo, useContext } from 'react'
import { SnackBarContext } from '../../../../../Components/SnackBar'
import { CampaignContext, onExportData, openLinkInNewTab } from './utils'
import { EmailModal } from './components/EmailModal'
import { apiXero } from '../../../../../Module/API/Xero'
import { PATHS } from '../../../../../Config/Routing/paths'
import XeroConnected from '../../../../../Components/XeroConnected'
import { useQuery } from 'react-query'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { Board } from './components/Board'
import {
  useSearchParams,
  useCityFilter,
  useDateRangeFilter,
  useStatus,
  SearchBar,
  CitySelect,
  FilterChips,
  DatePicker,
} from './components/Filter'
import { campaignsBuilderData } from './graphQL'
import { useDebounce } from '../../../../../shared/libraries/utils/func'
import { pickBy } from 'lodash'
import { CAMPAIGN_ENUM_TYPES, CAMPAIGN_INTERNAL_STATUSES, CAMPAIGN_LAST_ACTIVE_STEPS, CAMPAIGN_DRAFT_STEPS, CAMPAIGN_SUBTYPES_ENUM } from '../../../../../shared/constants'
import moment from 'moment'
import LinearProgress from '@material-ui/core/LinearProgress'
/** @import { Campaign, Country, Cities } from './utils' */
/** @import { StatusOptions, DateRange } from './components/Filter' */

/**
 * @param {{
 *   country: Country
 * }} props
 */
function CampaignPipelineView({ country }) {
  const runSnackBar = useContext(SnackBarContext)
  const [searchString, setSearchString] = useSearchParams('searchString')
  const { city, setCity } = useCityFilter(country.cities)
  const { dateRange, setDateRange } = useDateRangeFilter()
  const { status, setStatus } = useStatus()
  const dsearchString = useDebounce(searchString, 300)
  const [campaignEmailData, setCampaignEmailData] = useState(/** @type {unknown} */(null))

  const flatInternalStatus = useMemo(() => {
    const internalStatus = Object.keys(pickBy(status.submitted))

    const hasDraft = Object.keys(pickBy(status.draft)).length > 0
    if (hasDraft) {
      internalStatus.push(CAMPAIGN_INTERNAL_STATUSES.DRAFT)
    }

    const hasRejected = status.draft[CAMPAIGN_DRAFT_STEPS.REJECTED]
    if (hasRejected) {
      internalStatus.push(CAMPAIGN_INTERNAL_STATUSES.REJECTED)
    }

    return internalStatus
  }, [status])

  const campaigns = useQuery(
    ['CampaignPipelineData', country.id, flatInternalStatus, dateRange],
    async () =>
      await campaignsBuilderData({
        countryId: country.id,
        includePathfinder: true,
        internalStatus: flatInternalStatus,
        dateRange: {
          start: moment(dateRange.start).utc().format('YYYY-MM-DD'),
          end: moment(dateRange.end).utc().format('YYYY-MM-DD'),
        },
        type: [
          CAMPAIGN_ENUM_TYPES.HANDTOHAND,
          CAMPAIGN_ENUM_TYPES.LETTERBOX,
        ],
        subtype: [
          CAMPAIGN_SUBTYPES_ENUM.EDDM,
          CAMPAIGN_SUBTYPES_ENUM.ADM,
        ],
      }).then(formatCampaignStatus),
    { keepPreviousData: true },
  )

  const formattedCampaigns = useMemo(() => {
    if (!campaigns.data) {
      return []
    }

    const sorted = campaigns.data.sort((x, y) =>
      Number(y.internalStatusChangedDate) - Number(x.internalStatusChangedDate)
    )

    return filterCampaigns({ campaigns: sorted, dsearchString, status, city })
  }, [dsearchString, status, city, campaigns.data])

  if (campaigns.error) {
    runSnackBar({
      type: 'error',
      msg: `failed to load campaigns ${campaigns.error}`,
    })
  }

  const tenant = useQuery(['getXeroTenant'], async () => {
    const result = await apiXero.getXeroTenant()
    if (result && result.isAuthorized && result.isConnected) {
      return result
    } else {
      return null
    }
  })

  return (
    <CampaignContext.Provider value={{ country, campaignEmailData, setCampaignEmailData }}>
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <EmailModal tenant={tenant} />
        <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'center' }}>
          <Box style={{ display: 'flex', gap: 15 }}>
            <SearchBar
              value={searchString || ''}
              onChange={searchString => setSearchString(searchString)}
            />
            <CitySelect
              value={city}
              onChange={city => setCity(city)}
              cities={country.cities.edges.map(({ node }) => node.name)}
            />
            <DatePicker
              value={dateRange}
              onChange={dateRange => setDateRange(dateRange)}
            />
          </Box>
          <XeroControls
            tenant={tenant}
            campaigns={formattedCampaigns}
            countryName={country.name}
          />
        </Box>
        <FilterChips status={status} onChange={setStatus} />

        <Box>
          <LinearProgress variant={campaigns.isFetching ? "indeterminate" : "determinate"} value={campaigns.isFetching ? 0 : 100} />
          <Board data={formattedCampaigns} status={status} />
        </Box>
      </Box>
    </CampaignContext.Provider>
  )
}

/**
 * @param {{
 *   campaigns: Campaign[],
 *   dsearchString: string | null, 
 *   status: StatusOptions, 
 *   city: string,
 * }} args
 */
function filterCampaigns({ campaigns, dsearchString, status, city }) {
  return campaigns.filter(c => {
    const searchFilter = [
      c.campaignId,
      c.campaignName,
      c.clientName,
      c.totalCosts,
      c.flyerType,
      c.flyerWeight,
      c.submitter && c.submitter.lastName,
      c.submitter && c.submitter.firstName,
      c.type,
      c.subtype,
      c.cityName,
    ]
      .map(x => (x ? String(x).toLowerCase() : ''))
      .some(x => x.includes((dsearchString || '').toLowerCase()))

    const statusFilter =
      status.submitted[c.internalStatus] || (c.draftStep && status.draft[c.draftStep])

    const cityFilter =
      city ? (c.cityName || "") === city : true

    return searchFilter && cityFilter && statusFilter
  })
}

/**
 * @param {{
 *   tenant: {
 *     isLoading: boolean,
 *     data: unknown
 *   }
 *   campaigns: Campaign[]
 *   countryName: string
 * }} props
 */
function XeroControls({ tenant, campaigns, countryName }) {
  const isXeroAvailable = apiXero.isHaveXeroAccess()

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'flex-end',
      }}
    >
      <Box style={{ display: 'flex', gap: 10 }}>
        <Typography>Xero Status:</Typography>
        <XeroConnected isConnected={tenant.data} />
      </Box>
      <Box style={{ display: 'flex', gap: 8 }}>
        <StyledButton onClick={() => onExportData(campaigns, countryName)}>
          Export
        </StyledButton>
        <StyledButton
          disabled={!isXeroAvailable}
          onClick={() => {
            openLinkInNewTab(
              `${PATHS.main.dashboard}${PATHS.dashboard.settings}${PATHS.settings.xeroConnection
              }`,
            )
          }}
        >
          Xero Settings
        </StyledButton>
      </Box>
    </Box>
  )
}

/**
 * @param {import('@material-ui/core/Button').ButtonProps} props
 */
function StyledButton({ disabled, children, ...props }) {
  return (
    <Button
      {...props}
      disabled={disabled}
      variant="contained"
      style={{
        backgroundColor: disabled ? undefined : '#004360',
        color: disabled ? undefined : 'white',
        paddingTop: 9,
        paddingBottom: 9,
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      {children}
    </Button>
  )
}

/** creates the draftStep field for draft board columns
 * @param {Omit<Campaign,'draftStep'>[]} data
 * @returns {Campaign[]}
 */
function formatCampaignStatus(data) {
  const format = data.map((node) => {
    let draftStep;

    //group together a few draft substatus 
    if (node.internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT) {
      const entryStatuses = [CAMPAIGN_LAST_ACTIVE_STEPS.AUDIENCE, CAMPAIGN_LAST_ACTIVE_STEPS.SEGMENTS]
      draftStep = entryStatuses.includes(node.lastActiveStep) ? CAMPAIGN_DRAFT_STEPS.ENTRY : node.lastActiveStep
    }

    //treat reject status as a draft substatus
    if (node.internalStatus === CAMPAIGN_INTERNAL_STATUSES.REJECTED) {
      draftStep = CAMPAIGN_DRAFT_STEPS.REJECTED
    }

    return { ...node, draftStep }
  })

  return format
}

export default memo(CampaignPipelineView)
